import React from 'react';
import { Monitor, Keyboard, Type, Mic2, AppWindow, Settings, Zap, Lightbulb, Camera, FileAudio, Download } from 'lucide-react';
import Card from '../../../components/Card/Card';
import SectionHeader from '../../../components/SectionHeader/SectionHeader';

function BureauTab() {
  return (
    <>
      {/* Section: Introduction */}
      <Card className="card-large">
        <SectionHeader icon={Monitor} title="Application bureau" variant="default" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Monitor className="guide-info-icon" size={20} />
            <p>
              L'application bureau (Windows/Mac) offre des fonctionnalités avancées :
              <strong> macros, raccourcis clavier, support dictaphone Philips</strong>,
              et enregistrement en arrière-plan.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Macros */}
      <Card className="card-large">
        <SectionHeader icon={Type} title="Macros (raccourcis texte)" variant="info" />
        <div className="guide-section-content">
          <span className="guide-badge-gold">Gold / Enterprise</span>
          <div className="guide-tip" style={{ marginTop: '15px' }}>
            <Zap className="guide-tip-icon" size={20} />
            <p>
              Les macros sont des <strong>raccourcis texte</strong>. Tapez un mot-clé court
              et il est automatiquement remplacé par un texte plus long.
            </p>
          </div>
          <div className="guide-steps">
            <div className="guide-step">
              <span className="guide-step-number">1</span>
              <div className="guide-step-content">
                <h4>Créer une macro</h4>
                <p>Réglages → Macros → Ajouter. Définissez le mot-clé et le texte.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">2</span>
              <div className="guide-step-content">
                <h4>Utiliser la macro</h4>
                <p>Tapez le mot-clé dans n'importe quelle application.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">3</span>
              <div className="guide-step-content">
                <h4>Expansion automatique</h4>
                <p>Le texte complet apparaît instantanément.</p>
              </div>
            </div>
          </div>
          <div className="guide-info">
            <Lightbulb className="guide-info-icon" size={20} />
            <p>
              <strong>Exemple :</strong> "sig" devient "Dr Martin, Cardiologue, Hôpital Universitaire"
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Raccourcis clavier */}
      <Card className="card-large">
        <SectionHeader icon={Keyboard} title="Raccourcis clavier" variant="success" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Keyboard className="guide-info-icon" size={20} />
            <p>
              De nombreux raccourcis clavier sont disponibles : afficher/masquer Voxia,
              démarrer l'enregistrement, capture d'écran, Dictée Live, et bien d'autres.
              Tous sont <strong>personnalisables dans Réglages → Raccourcis clavier</strong>.
            </p>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              Les raccourcis fonctionnent même quand Voxia est minimisé ou en arrière-plan.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Capture d'écran */}
      <Card className="card-large">
        <SectionHeader icon={Camera} title="Capture d'écran" variant="info" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Camera className="guide-info-icon" size={20} />
            <p>
              Capturez n'importe quelle zone de votre écran et envoyez-la <strong>directement dans l'annexe</strong>
              de votre dictée. Le texte est automatiquement extrait de l'image.
            </p>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              Idéal pour capturer un résultat de labo, un ancien compte-rendu ou tout document
              affiché à l'écran sans avoir à l'imprimer ou le scanner.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Dictaphone Philips DPM */}
      <Card className="card-large">
        <SectionHeader icon={Mic2} title="Dictaphone Philips DPM" variant="default" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Mic2 className="guide-info-icon" size={20} />
            <p>
              Vous utilisez un dictaphone <strong>Philips DPM</strong> ? Voxia peut <strong>récupérer automatiquement</strong>
              vos enregistrements quand vous branchez l'appareil en USB.
            </p>
          </div>
          <div className="guide-steps">
            <div className="guide-step">
              <span className="guide-step-number">1</span>
              <div className="guide-step-content">
                <h4>Configurez le profil d'enregistrement</h4>
                <p>Sur votre Philips DPM, sélectionnez un profil d'enregistrement en <strong>MP3</strong>.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">2</span>
              <div className="guide-step-content">
                <h4>Activez la récupération auto</h4>
                <p>Dans Voxia : Réglages → Dictaphone → Récupération automatique</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">3</span>
              <div className="guide-step-content">
                <h4>Branchez et c'est parti</h4>
                <p>Voxia détecte l'appareil et transcrit automatiquement les fichiers MP3.</p>
              </div>
            </div>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              Voxia n'interfère pas avec Winscribe ou d'autres logiciels de dictée existants.
              Vous pouvez utiliser les deux en parallèle.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Import de fichiers MP3 */}
      <Card className="card-large">
        <SectionHeader icon={FileAudio} title="Import de fichiers audio" variant="info" />
        <div className="guide-section-content">
          <div className="guide-info">
            <FileAudio className="guide-info-icon" size={20} />
            <p>
              <strong>Glissez-déposez</strong> n'importe quel fichier MP3 directement dans la fenêtre
              du dictaphone pour le transcrire instantanément.
            </p>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              Pratique pour transcrire des enregistrements provenant d'autres sources
              ou d'un ancien dictaphone.
            </p>
          </div>
        </div>
      </Card>

  

      {/* Section: Installation */}
      <Card className="card-large">
        <SectionHeader icon={Download} title="Application portable" variant="success" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Download className="guide-info-icon" size={20} />
            <p>
              Voxia est une <strong>application portable</strong> : pas d'installation requise.
              Téléchargez, lancez, et c'est parti. Fonctionne sur la plupart des ordinateurs,
              <strong> y compris professionnels</strong>, sans intervention du service informatique.
            </p>
          </div>
          <div className="guide-features">
            <div className="guide-feature">
              <div className="guide-feature-icon primary">
                <Monitor size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Windows 10/11</h4>
                <p>Téléchargez depuis voxia-md.ch/download</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon gray">
                <Monitor size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>macOS 10.12+</h4>
                <p>Compatible Intel et Apple Silicon</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}

export default BureauTab;
